import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {Observable, Subject, switchMap} from 'rxjs';
import {environment} from 'src/environments/environment';
import {
  IBankInfo,
  IBankInfoAgreement,
  IBankInfoCards,
  IBankInfoCredential,
  IBankInfoDropdowns,
  IPETab,
} from '../interfaces/accountant.interfaces';
import {IEmployeeBCoin} from '../interfaces/bcoin.interface';
import {IDropdownObject} from '../interfaces/dropdown-object.interface';
import {IAccessData, Tab} from '../interfaces/employee.interface';
import {IGeneralTab} from '../interfaces/general-data.interface';
import {
  IIncreaseHistoryTable,
  IPostTableData,
} from '../interfaces/general-tables.interface';
import {IMaternityPeriod} from '../interfaces/maternity.interface';
import {IPersonalData} from '../interfaces/personal-data.interface';
import {
  IProfessionalTab,
  ITrainingsTables,
} from '../interfaces/professional-tab.interface';
import {ProfessionalTableType} from '../shared/ui-kits/table/table.models';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private url = environment.API + 'api/v1/';
  private urlEmployee = environment.API + 'api/v1/Employee/';
  public readonly getEmployeeFullNameStream = new Subject<string>();

  constructor(private http: HttpClient) {}

  public getDropdownItems<T>(tab: Tab): Observable<T> {
    return this.http.get<T>(`${this.urlEmployee}${tab}/editMode`);
  }

  public getTabData<T>(tab: Tab, employeeId: number): Observable<T> {
    return this.http.get<T>(`${this.urlEmployee}${employeeId}/${tab}`);
  }

  public postTabData(
    tabName: Tab,
    data: IPersonalData | IProfessionalTab
  ): Observable<IPersonalData | IProfessionalTab> {
    return this.http.put<IPersonalData | IProfessionalTab>(
      `${this.urlEmployee}${tabName}`,
      data
    );
  }

  public updateProfessionalTables(
    tableName: ProfessionalTableType,
    tableData: IPostTableData,
    employeeId: number
  ): Observable<IProfessionalTab> {
    return this.http
      .put<void>(`${this.urlEmployee}${tableName}`, tableData)
      .pipe(
        switchMap(() =>
          this.getTabData<IProfessionalTab>(Tab.Professional, employeeId)
        )
      );
  }

  public addTraining(
    tableData: ITrainingsTables,
    employeeId: number
  ): Observable<IProfessionalTab> {
    return this.http
      .post<void>(`${this.urlEmployee}trainings`, tableData)
      .pipe(
        switchMap(() =>
          this.getTabData<IProfessionalTab>(Tab.Professional, employeeId)
        )
      );
  }

  public updateTraining(
    updatedValue: ITrainingsTables,
    employeeId: number
  ): Observable<IProfessionalTab> {
    return this.http
      .put<void>(`${this.urlEmployee}trainings`, updatedValue)
      .pipe(
        switchMap(() =>
          this.getTabData<IProfessionalTab>(Tab.Professional, employeeId)
        )
      );
  }

  public deleteTraining(
    trainingId: number,
    employeeId: number
  ): Observable<IProfessionalTab> {
    return this.http
      .delete<void>(`${this.urlEmployee}trainings/${employeeId}/${trainingId}`)
      .pipe(
        switchMap(() =>
          this.getTabData<IProfessionalTab>(Tab.Professional, employeeId)
        )
      );
  }

  public addNewProfessionalTableData(
    tableName: ProfessionalTableType,
    tableData: IPostTableData,
    employeeId: number
  ): Observable<IProfessionalTab> {
    return this.http
      .post<void>(`${this.urlEmployee}${employeeId}/${tableName}`, tableData)
      .pipe(
        switchMap(() =>
          this.getTabData<IProfessionalTab>(Tab.Professional, employeeId)
        )
      );
  }

  public addMaternity(
    maternityPeriod: IMaternityPeriod
  ): Observable<IGeneralTab> {
    return this.http
      .post<void>(`${this.urlEmployee}general/maternityPeriod`, maternityPeriod)
      .pipe(
        switchMap(() =>
          this.getTabData<IGeneralTab>(Tab.General, maternityPeriod.employeeId)
        )
      );
  }

  public updateMaternity(body: IMaternityPeriod): Observable<IGeneralTab> {
    return this.http
      .put<void>(`${this.urlEmployee}general/maternityPeriod`, body)
      .pipe(
        switchMap(() =>
          this.getTabData<IGeneralTab>(Tab.General, body.employeeId)
        )
      );
  }
  public deleteMaternity(
    id: number,
    employeeId: number
  ): Observable<IGeneralTab> {
    return this.http
      .delete<void>(`${this.urlEmployee}general/maternityPeriod/${id}`)
      .pipe(
        switchMap(() => this.getTabData<IGeneralTab>(Tab.General, employeeId))
      );
  }

  public getSalaryData(
    id: number
  ): Observable<{salaries: IIncreaseHistoryTable[]}> {
    return this.http.get<{salaries: IIncreaseHistoryTable[]}>(
      `${this.url}Salary/${id}`
    );
  }

  public getPEData(employeeId: number): Observable<IPETab> {
    return this.http.get<IPETab>(`${this.urlEmployee}${employeeId}/pe`);
  }

  getBankInfoDropdowns(employeeId: number): Observable<IBankInfoDropdowns> {
    return this.http.get<IBankInfoDropdowns>(
      `${this.urlEmployee}${employeeId}/bankInfo/enums`
    );
  }

  public addNewPE(data: IPETab): Observable<IPETab> {
    return this.http.post<IPETab>(`${this.urlEmployee}pe/add`, data);
  }

  public updatePE(data: IPETab): Observable<IPETab> {
    return this.http.put<IPETab>(`${this.urlEmployee}pe/update`, data);
  }

  public isEmployeeHasPE(employeeId: number): Observable<number> {
    return this.http.get<number>(`${this.urlEmployee}${employeeId}/PEId`);
  }

  public getEmployeeFullname(id: number): Observable<string> {
    return this.http.get<string>(`${this.urlEmployee}${id}/fullname`, {
      responseType: 'text' as 'json',
    });
  }

  public getBankInfo(
    employeeId: number,
    bankId?: number
  ): Observable<IBankInfo> {
    if (!bankId)
      return this.http.get<IBankInfo>(
        `${this.urlEmployee}${employeeId}/bankInfo`
      );
    return this.http.get<IBankInfo>(
      `${this.urlEmployee}${employeeId}/bankInfo?bankId=${bankId}`
    );
  }

  public deleteCard(cardId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.urlEmployee}bankInfo/${cardId}/deleteCard`
    );
  }

  public deleteAgreement(agreementId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.urlEmployee}bankInfo/${agreementId}/deleteAgreement`
    );
  }

  public deleteCredential(credentialId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.urlEmployee}bankInfo/${credentialId}/deleteCredential`
    );
  }

  public updateCardNumber(cardInfomation: IBankInfoCards): Observable<void> {
    return this.http.put<void>(
      `${this.urlEmployee}bankInfo/updateCard`,
      cardInfomation
    );
  }

  public addNewCard(cardInfomation: IBankInfoCards): Observable<void> {
    return this.http.post<void>(
      `${this.urlEmployee}bankInfo/addCard`,
      cardInfomation
    );
  }

  public updateCredential(
    credentialInfo: IBankInfoCredential
  ): Observable<void> {
    return this.http.put<void>(
      `${this.urlEmployee}bankInfo/updateCredential`,
      credentialInfo
    );
  }

  public addNewCredential(
    credentialInfo: IBankInfoCredential
  ): Observable<void> {
    return this.http.post<void>(
      `${this.urlEmployee}bankInfo/addCredential`,
      credentialInfo
    );
  }

  public updateAgreement(agreementInfo: IBankInfoAgreement): Observable<void> {
    return this.http.put<void>(
      `${this.urlEmployee}bankInfo/updateAgreement`,
      agreementInfo
    );
  }

  public addNewAgreement(agreementInfo: IBankInfoAgreement): Observable<void> {
    return this.http.post<void>(
      `${this.urlEmployee}bankInfo/addAgreement`,
      agreementInfo
    );
  }

  public addNewBankInfo(bankInfo: IBankInfo): Observable<number> {
    return this.http.post<number>(`${this.urlEmployee}bankInfo/new`, bankInfo);
  }

  public updateBankInfo(bankInfo: IBankInfo): Observable<void> {
    return this.http.put<void>(`${this.urlEmployee}bankInfo/update`, bankInfo);
  }

  public getEmployeeStatus(employeeId: number): Observable<IDropdownObject> {
    return this.http.get<IDropdownObject>(
      `${this.urlEmployee}${employeeId}/status`
    );
  }

  public getEmployeePhoto(id: number): Observable<Blob> {
    return this.http.get<Blob>(`${this.urlEmployee}${id}/photo`, {
      responseType: 'blob' as 'json',
    });
  }

  public updateEmployeePhoto(id: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('photoFile', file);
    return this.http.put<void>(`${this.urlEmployee}${id}/photo`, formData);
  }

  public getCommentFile(id: number): Observable<Blob> {
    return this.http.get<Blob>(`${this.urlEmployee}${id}/CommentFile`, {
      responseType: 'blob' as 'json',
    });
  }

  public updateCommentFile(id: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('commentFile', file);
    return this.http.put<void>(
      `${this.urlEmployee}${id}/CommentFile`,
      formData
    );
  }

  public deleteCommentFile(id: number): Observable<void> {
    return this.http.delete<void>(`${this.urlEmployee}${id}/CommentFile`);
  }

  public getEmployeeBCoinScore(employeeId: number): Observable<IEmployeeBCoin> {
    return this.http.post<IEmployeeBCoin>(
      `${this.urlEmployee}BCoin/${employeeId}`,
      {}
    );
  }

  public getAllEmployeeBCoinScores(): Observable<IEmployeeBCoin[]> {
    return this.http.get<IEmployeeBCoin[]>(`${this.urlEmployee}BCoin/All`);
  }

  public searchEmployeeBcoinScores(
    searchWord: string
  ): Observable<IEmployeeBCoin[]> {
    return this.http.get<IEmployeeBCoin[]>(
      `${this.urlEmployee}BCoin/${searchWord}`
    );
  }

  public updateEmployeeBCoinScore(data: IEmployeeBCoin): Observable<void> {
    return this.http.put<void>(`${this.urlEmployee}BCoin/update`, data);
  }

  public getUserAccessData(employeeId: number): Observable<IAccessData> {
    return this.http.get<IAccessData>(
      `${this.urlEmployee}AccessData/${employeeId}`
    );
  }

  public getPersonalEmail(employeeId: number): Observable<string> {
    return this.http.get<string>(
      `${this.urlEmployee}PersonalEmail/${employeeId}`,
      {
        responseType: 'text' as 'json',
      }
    );
  }

  public getCurrentEmploymentForm(
    employeeId: number
  ): Observable<IDropdownObject> {
    return this.http.get<IDropdownObject>(
      `${this.urlEmployee}${employeeId}/EmploymentForm/Current`
    );
  }
}
